/**
 * Actions — self-service forms that trigger handlers (webhook / Celery /
 * GitHub workflow dispatch). Port-style action catalog.
 *
 * Three views combined:
 *   - Action library (left)
 *   - Invoke form for the selected action (right, top)
 *   - Approval queue (pending executions, bottom)
 */
import { useState, useEffect, useCallback } from 'react'
import {
  Zap, Play, Lock, Plus, RefreshCw, CheckCircle2, XCircle, Clock3,
  AlertCircle, ShieldCheck, ShieldX, User,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'

interface FormField {
  name: string
  title?: string
  type: string
  required?: boolean
  enum?: string[] | null
  default?: any
  description?: string
}

interface Action {
  identifier: string
  title: string
  description: string
  icon: string
  trigger: string
  blueprint_identifier: string
  form_schema: FormField[]
  handler_type: string
  requires_approval: boolean
  allowed_roles: string[]
}

interface Execution {
  id: number
  action_identifier: string
  entity_identifier: string
  params: Record<string, any>
  status: string
  requested_by: string
  approved_by: string
  result: any
  created_at: string
  completed_at: string | null
}

const STATUS_COLOR: Record<string, string> = {
  pending:  'bg-amber/20 text-amber border-amber/40',
  approved: 'bg-sky/20 text-sky border-sky/40',
  running:  'bg-sky/20 text-sky border-sky/40',
  success:  'bg-emerald/20 text-emerald border-emerald/40',
  failed:   'bg-rose/20 text-rose border-rose/40',
  rejected: 'bg-rose/10 text-rose/70 border-rose/30',
}

const STATUS_ICON: Record<string, React.ElementType> = {
  pending: Clock3, approved: Play, running: Play,
  success: CheckCircle2, failed: XCircle, rejected: XCircle,
}

export default function ActionsPage() {
  const auth = useAuth()
  const [actions, setActions] = useState<Action[]>([])
  const [selected, setSelected] = useState<Action | null>(null)
  const [params, setParams] = useState<Record<string, any>>({})
  const [executions, setExecutions] = useState<Execution[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastResult, setLastResult] = useState<Execution | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [actRes, exRes] = await Promise.all([
        api.get('/api/actions'),
        api.get('/api/actions/executions', { params: { limit: 50 } }),
      ])
      setActions(actRes.data.actions || [])
      setExecutions(exRes.data.executions || [])
      setError(null)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Load failed')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const selectAction = (a: Action) => {
    setSelected(a)
    const defaults: Record<string, any> = {}
    for (const f of a.form_schema || []) {
      if (f.default !== undefined) defaults[f.name] = f.default
      else if (f.type === 'boolean') defaults[f.name] = false
      else defaults[f.name] = ''
    }
    setParams(defaults)
    setLastResult(null)
  }

  const invoke = async () => {
    if (!selected) return
    setBusy(true)
    try {
      const { data } = await api.post(`/api/actions/${selected.identifier}/invoke`, {
        entity_identifier: '',
        params,
      })
      setLastResult(data)
      await load()
    } catch (e: any) {
      const d = e?.response?.data?.detail
      setError(typeof d === 'string' ? d : JSON.stringify(d))
    } finally {
      setBusy(false)
    }
  }

  const approve = async (id: number) => {
    try {
      await api.post(`/api/actions/executions/${id}/approve`)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Approve failed')
    }
  }

  const reject = async (id: number) => {
    try {
      await api.post(`/api/actions/executions/${id}/reject`)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Reject failed')
    }
  }

  const seed = async () => {
    setBusy(true)
    try {
      await api.post('/api/actions/seed')
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Seed failed')
    } finally {
      setBusy(false)
    }
  }

  const renderInput = (f: FormField, val: any, onChange: (v: any) => void) => {
    if (f.type === 'boolean') {
      return <input type="checkbox" checked={!!val} onChange={e => onChange(e.target.checked)} />
    }
    if (f.type === 'select' && f.enum) {
      return (
        <select
          value={val ?? ''}
          onChange={e => onChange(e.target.value)}
          className="w-full px-2 py-1.5 bg-gunmetal/30 border border-gunmetal rounded text-sm"
        >
          <option value="">—</option>
          {f.enum.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      )
    }
    if (f.type === 'number') {
      return (
        <input
          type="number"
          value={val ?? ''}
          onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className="w-full px-2 py-1.5 bg-gunmetal/30 border border-gunmetal rounded text-sm font-mono"
        />
      )
    }
    return (
      <input
        type={f.type === 'email' ? 'email' : (f.type === 'url' ? 'url' : 'text')}
        value={val ?? ''}
        onChange={e => onChange(e.target.value)}
        placeholder={f.description}
        className="w-full px-2 py-1.5 bg-gunmetal/30 border border-gunmetal rounded text-sm"
      />
    )
  }

  const pending = executions.filter(e => e.status === 'pending')
  const recent = executions.filter(e => e.status !== 'pending').slice(0, 10)
  const isAdmin = auth.email && (auth as any).role === 'admin'

  return (
    <div className="min-h-screen bg-graphite text-whitesmoke">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gunmetal">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-3">
              <Zap className="w-6 h-6 text-sky" />
              Self-service Actions
            </h1>
            <p className="text-sm text-whitesmoke/60 mt-1">
              Run pre-defined workflows against your catalog. Gated actions require admin approval.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={seed}
              disabled={busy}
              className="px-3 py-1.5 rounded bg-gunmetal hover:bg-gunmetal/80 text-sm flex items-center gap-1.5"
            >
              <RefreshCw className={clsx('w-4 h-4', busy && 'animate-spin')} />
              Seed sample actions
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-rose/10 border border-rose/30 text-rose text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-rose/60 hover:text-rose text-xs">
              dismiss
            </button>
          </div>
        )}

        {pending.length > 0 && (
          <div className="mb-4 border border-amber/40 rounded overflow-hidden">
            <div className="bg-amber/20 px-3 py-2 text-xs uppercase tracking-wider text-amber">
              Approval queue — {pending.length} pending
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs text-whitesmoke/60">
                <tr className="border-b border-gunmetal/50">
                  <th className="text-left px-3 py-1.5 font-normal">Action</th>
                  <th className="text-left px-3 py-1.5 font-normal">Requested by</th>
                  <th className="text-left px-3 py-1.5 font-normal">Params</th>
                  <th className="text-right px-3 py-1.5 font-normal"></th>
                </tr>
              </thead>
              <tbody>
                {pending.map(e => (
                  <tr key={e.id} className="border-b border-gunmetal/30">
                    <td className="px-3 py-2 font-mono text-xs">{e.action_identifier}</td>
                    <td className="px-3 py-2 text-xs">{e.requested_by}</td>
                    <td className="px-3 py-2 text-xs text-whitesmoke/70">
                      {Object.entries(e.params || {}).map(([k, v]) => `${k}=${v}`).join(', ')}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => approve(e.id)}
                        className="text-xs px-2 py-1 rounded bg-emerald/20 hover:bg-emerald/30 text-emerald mr-2 flex items-center gap-1 inline-flex"
                      >
                        <ShieldCheck className="w-3 h-3" /> approve
                      </button>
                      <button
                        onClick={() => reject(e.id)}
                        className="text-xs px-2 py-1 rounded bg-rose/20 hover:bg-rose/30 text-rose flex items-center gap-1 inline-flex"
                      >
                        <ShieldX className="w-3 h-3" /> reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Action library */}
          <div className="col-span-4">
            <div className="border border-gunmetal rounded overflow-hidden">
              <div className="bg-gunmetal/40 px-3 py-2 text-xs uppercase tracking-wider text-whitesmoke/60">
                Actions ({actions.length})
              </div>
              {loading && actions.length === 0 && (
                <div className="p-4 text-sm text-whitesmoke/60">Loading…</div>
              )}
              {!loading && actions.length === 0 && (
                <div className="p-6 text-center text-sm text-whitesmoke/50">
                  No actions. Click <strong>Seed sample actions</strong>.
                </div>
              )}
              <ul>
                {actions.map(a => (
                  <li key={a.identifier}>
                    <button
                      onClick={() => selectAction(a)}
                      className={clsx(
                        'w-full text-left px-3 py-2.5 border-b border-gunmetal/50 hover:bg-gunmetal/30',
                        selected?.identifier === a.identifier && 'bg-sky/10 border-l-2 border-l-sky',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-whitesmoke/60" />
                        <span className="font-medium">{a.title}</span>
                        {a.requires_approval && (
                          <Lock className="w-3 h-3 text-amber" />
                        )}
                      </div>
                      <div className="text-xs text-whitesmoke/50 mt-0.5">
                        {a.description}
                      </div>
                      <div className="text-[10px] text-whitesmoke/40 mt-1 font-mono flex gap-2">
                        <span>{a.blueprint_identifier || '*'}</span>
                        <span>·</span>
                        <span>{a.handler_type}</span>
                        <span>·</span>
                        <span>{a.trigger}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Invoke form */}
          <div className="col-span-8">
            {!selected && (
              <div className="border border-gunmetal rounded p-12 text-center text-whitesmoke/40">
                Select an action to invoke it.
              </div>
            )}
            {selected && (
              <div className="border border-gunmetal rounded">
                <div className="bg-gunmetal/40 px-4 py-3">
                  <div className="text-xs uppercase tracking-wider text-whitesmoke/70">Invoke</div>
                  <div className="text-lg font-semibold flex items-center gap-2">
                    {selected.title}
                    {selected.requires_approval && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber/20 text-amber text-xs">
                        <Lock className="w-3 h-3" /> requires approval
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-whitesmoke/60 mt-1">{selected.description}</div>
                </div>
                <div className="p-4 space-y-3">
                  {selected.form_schema.length === 0 && (
                    <div className="text-xs text-whitesmoke/50">No parameters.</div>
                  )}
                  {selected.form_schema.map(f => (
                    <label key={f.name} className="block">
                      <span className="text-xs text-whitesmoke/70 flex items-center gap-1">
                        {f.title || f.name}
                        {f.required && <span className="text-rose">*</span>}
                      </span>
                      <div className="mt-1">
                        {renderInput(f, params[f.name], v => setParams({ ...params, [f.name]: v }))}
                      </div>
                      {f.description && (
                        <div className="text-[10px] text-whitesmoke/40 mt-0.5">{f.description}</div>
                      )}
                    </label>
                  ))}
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={invoke}
                      disabled={busy}
                      className="px-4 py-1.5 rounded bg-sky/20 hover:bg-sky/30 text-sky text-sm flex items-center gap-2"
                    >
                      <Play className="w-4 h-4" />
                      {selected.requires_approval ? 'Request execution' : 'Run now'}
                    </button>
                  </div>

                  {lastResult && (
                    <div className={clsx(
                      'mt-2 p-3 rounded border text-sm',
                      STATUS_COLOR[lastResult.status] || STATUS_COLOR.success,
                    )}>
                      <div className="font-semibold">Status: {lastResult.status}</div>
                      {lastResult.result?.detail && (
                        <div className="text-xs mt-1 opacity-80">{lastResult.result.detail}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recent executions */}
            {recent.length > 0 && (
              <div className="mt-4 border border-gunmetal rounded overflow-hidden">
                <div className="bg-gunmetal/40 px-3 py-2 text-xs uppercase tracking-wider text-whitesmoke/60">
                  Recent executions
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-whitesmoke/60">
                    <tr className="border-b border-gunmetal/50">
                      <th className="text-left px-3 py-1.5 font-normal">Action</th>
                      <th className="text-left px-3 py-1.5 font-normal">Status</th>
                      <th className="text-left px-3 py-1.5 font-normal">By</th>
                      <th className="text-left px-3 py-1.5 font-normal">When</th>
                      <th className="text-left px-3 py-1.5 font-normal">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map(e => {
                      const Icon = STATUS_ICON[e.status] || Clock3
                      return (
                        <tr key={e.id} className="border-b border-gunmetal/30">
                          <td className="px-3 py-2 font-mono text-xs">{e.action_identifier}</td>
                          <td className="px-3 py-2">
                            <span className={clsx(
                              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-xs',
                              STATUS_COLOR[e.status],
                            )}>
                              <Icon className="w-3 h-3" /> {e.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs">{e.requested_by}</td>
                          <td className="px-3 py-2 text-xs text-whitesmoke/60">
                            {new Date(e.created_at).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-xs text-whitesmoke/70 truncate max-w-xs">
                            {e.result?.detail || '—'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
